import React, { useState, useRef, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { useSessionContext } from '../context/SessionContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card';
import Button from '../components/ui/Button';
import Input from '../components/ui/Input';
import Label from '../components/ui/Label';
import Textarea from '../components/ui/Textarea';
import { FiPlus, FiX, FiChevronDown, FiSearch, FiCheck } from 'react-icons/fi';
import { techniques as allTechniques } from '../data/techniques';
import { getTodayDateString } from '../lib/utils';

const SessionLog = () => {
  const navigate = useNavigate();
  const { addSession } = useSessionContext();
  const { register, handleSubmit, reset, formState: { errors } } = useForm({
    defaultValues: {
      date: getTodayDateString(),
      duration: '',
      sessionType: 'Rolling',
      partner: '',
      notes: '',
      wins: 0,
      losses: 0
    }
  });

  const [techniques, setTechniques] = useState([]);
  const [newTechnique, setNewTechnique] = useState({
    name: '',
    outcome: 'Attempted'
  });
  const [showDropdown, setShowDropdown] = useState(false);
  const [filteredTechniques, setFilteredTechniques] = useState([]);
  const dropdownRef = useRef(null);

  const sessionTypes = [
    'Rolling',
    'Drilling',
    'Competition Prep',
    'Fundamentals',
    'Open Mat',
    'Private Lesson',
    'Boxing',
    'MMA'
  ];

  const outcomes = ['Attempted', 'Succeeded', 'Failed', 'Taught'];

  useEffect(() => {
    if (newTechnique.name.trim()) {
      const filtered = allTechniques.filter((tech) =>
        tech.name.toLowerCase().includes(newTechnique.name.toLowerCase())
      );
      setFilteredTechniques(filtered);
    } else {
      setFilteredTechniques(allTechniques);
    }
  }, [newTechnique.name]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const addTechnique = () => {
    if (newTechnique.name.trim()) {
      setTechniques([...techniques, { ...newTechnique, id: Date.now() }]);
      setNewTechnique({ name: '', outcome: 'Attempted' });
      setShowDropdown(false);
    }
  };

  const selectTechniqueFromDropdown = (technique) => {
    setNewTechnique({ ...newTechnique, name: technique.name });
    setShowDropdown(false);
  };

  const removeTechnique = (id) => {
    setTechniques(techniques.filter((t) => t.id !== id));
  };

  const onSubmit = (data) => {
    const sessionData = {
      ...data,
      techniques,
      duration: parseInt(data.duration),
      wins: parseInt(data.wins) || 0,
      losses: parseInt(data.losses) || 0
    };
    addSession(sessionData);
    reset({
      date: getTodayDateString(),
      duration: '',
      sessionType: 'Rolling',
      partner: '',
      notes: '',
      wins: 0,
      losses: 0
    });
    setTechniques([]);
    navigate('/sessions');
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-3xl mx-auto">
      <div className="mb-6 sm:mb-8">
        <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 dark:text-white mb-2">
          Log Training Session
        </h1>
        <p className="text-sm sm:text-base text-slate-600 dark:text-slate-400">
          Record your mat time and track your progress.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg sm:text-xl">New Training Session</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-5 sm:space-y-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="date" className="text-sm sm:text-base">
                  Date
                </Label>
                <Input
                  id="date"
                  type="date"
                  {...register('date', { required: 'Date is required' })}
                  className="mt-1"
                />
                {errors.date && (
                  <p className="text-xs sm:text-sm text-red-600 dark:text-red-400 mt-1">
                    {errors.date.message}
                  </p>
                )}
              </div>

              <div>
                <Label htmlFor="duration" className="text-sm sm:text-base">
                  Duration (minutes)
                </Label>
                <Input
                  id="duration"
                  type="number"
                  placeholder="90"
                  {...register('duration', {
                    required: 'Duration is required',
                    min: { value: 1, message: 'Duration must be at least 1 minute' },
                    valueAsNumber: true
                  })}
                  className="mt-1"
                />
                {errors.duration && (
                  <p className="text-xs sm:text-sm text-red-600 dark:text-red-400 mt-1">
                    {errors.duration.message}
                  </p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="sessionType" className="text-sm sm:text-base">
                  Session Type
                </Label>
                <select
                  id="sessionType"
                  className="flex h-10 w-full rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent mt-1"
                  {...register('sessionType', { required: 'Session type is required' })}
                >
                  {sessionTypes.map((type) => (
                    <option key={type} value={type}>
                      {type}
                    </option>
                  ))}
                </select>
                {errors.sessionType && (
                  <p className="text-xs sm:text-sm text-red-600 dark:text-red-400 mt-1">
                    {errors.sessionType.message}
                  </p>
                )}
              </div>

              <div>
                <Label htmlFor="partner" className="text-sm sm:text-base">
                  Training Partner(s)
                </Label>
                <Input
                  id="partner"
                  placeholder="Mike, Sarah, John"
                  {...register('partner')}
                  className="mt-1"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="wins" className="text-sm sm:text-base">
                  Wins
                </Label>
                <Input
                  id="wins"
                  type="number"
                  min="0"
                  placeholder="0"
                  {...register('wins', {
                    valueAsNumber: true,
                    min: { value: 0, message: 'Cannot be negative' }
                  })}
                  className="mt-1"
                />
                {errors.wins && (
                  <p className="text-xs sm:text-sm text-red-600 dark:text-red-400 mt-1">
                    {errors.wins.message}
                  </p>
                )}
              </div>

              <div>
                <Label htmlFor="losses" className="text-sm sm:text-base">
                  Losses
                </Label>
                <Input
                  id="losses"
                  type="number"
                  min="0"
                  placeholder="0"
                  {...register('losses', {
                    valueAsNumber: true,
                    min: { value: 0, message: 'Cannot be negative' }
                  })}
                  className="mt-1"
                />
                {errors.losses && (
                  <p className="text-xs sm:text-sm text-red-600 dark:text-red-400 mt-1">
                    {errors.losses.message}
                  </p>
                )}
              </div>
            </div>

            <div>
              <Label className="text-sm sm:text-base font-medium">
                Techniques Practiced
              </Label>
              <p className="text-xs sm:text-sm text-slate-600 dark:text-slate-400 mb-3">
                Add techniques you worked on during this session.
              </p>

              <div className="space-y-3">
                <div className="flex flex-col sm:flex-row gap-2">
                  <div className="flex-1 relative" ref={dropdownRef}>
                    <div className="relative">
                      <Input
                        placeholder="Search or type technique name..."
                        value={newTechnique.name}
                        onChange={(e) => {
                          setNewTechnique({ ...newTechnique, name: e.target.value });
                          setShowDropdown(true);
                        }}
                        onFocus={() => setShowDropdown(true)}
                        className="pr-8"
                      />
                      <FiChevronDown className="w-4 h-4 absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 pointer-events-none" />
                    </div>

                    {showDropdown && (
                      <div className="absolute z-50 w-full mt-1 bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded-md shadow-lg max-h-60 overflow-y-auto">
                        {filteredTechniques.length > 0 ? (
                          <>
                            <div className="px-3 py-2 text-xs font-medium text-slate-500 dark:text-slate-400 bg-slate-50 dark:bg-slate-800/80 border-b border-slate-200 dark:border-slate-700 flex items-center">
                              <FiSearch className="w-3 h-3 mr-1" />
                              {filteredTechniques.length} technique
                              {filteredTechniques.length !== 1 ? 's' : ''} found
                            </div>
                            {filteredTechniques.map((tech) => (
                              <button
                                key={tech.id}
                                type="button"
                                onClick={() => selectTechniqueFromDropdown(tech)}
                                className="w-full text-left px-3 py-2 hover:bg-blue-50 dark:hover:bg-slate-700 border-b border-slate-100 dark:border-slate-700 last:border-b-0 transition-colors"
                              >
                                <div className="flex items-center justify-between">
                                  <div className="flex-1 min-w-0">
                                    <p className="text-sm font-medium text-slate-900 dark:text-slate-100 truncate">
                                      {tech.name}
                                    </p>
                                    <p className="text-xs text-slate-600 dark:text-slate-400 truncate">
                                      {tech.position} • {tech.type}
                                    </p>
                                  </div>
                                </div>
                              </button>
                            ))}
                          </>
                        ) : (
                          <div className="px-3 py-4 text-center">
                            <p className="text-sm text-slate-600 dark:text-slate-400 mb-2">
                              No techniques found
                            </p>
                            <p className="text-xs text-slate-500 dark:text-slate-500">
                              Type to create a new technique or add from library
                            </p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  <div className="flex gap-2">
                    <select
                      value={newTechnique.outcome}
                      onChange={(e) =>
                        setNewTechnique({ ...newTechnique, outcome: e.target.value })
                      }
                      className="flex-1 sm:flex-none px-3 py-2 border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      {outcomes.map((outcome) => (
                        <option key={outcome} value={outcome}>
                          {outcome}
                        </option>
                      ))}
                    </select>
                    <Button
                      type="button"
                      onClick={addTechnique}
                      size="md"
                      className="flex-shrink-0"
                    >
                      <FiPlus className="w-4 h-4" />
                    </Button>
                  </div>
                </div>

                {techniques.length > 0 && (
                  <div className="space-y-2">
                    {techniques.map((technique) => (
                      <div
                        key={technique.id}
                        className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-800/50 border border-slate-100 dark:border-slate-700 rounded-lg"
                      >
                        <div className="min-w-0 flex-1">
                          <span className="font-medium text-slate-900 dark:text-slate-100 text-sm sm:text-base break-words">
                            {technique.name}
                          </span>
                          <span
                            className={`ml-2 px-2 py-1 text-xs rounded-full inline-block ${
                              technique.outcome === 'Succeeded'
                                ? 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-400'
                                : technique.outcome === 'Failed'
                                ? 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-400'
                                : technique.outcome === 'Taught'
                                ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-400'
                                : 'bg-gray-100 text-gray-700 dark:bg-slate-700 dark:text-slate-300'
                            }`}
                          >
                            {technique.outcome}
                          </span>
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => removeTechnique(technique.id)}
                          className="flex-shrink-0 ml-2 text-slate-500 hover:text-red-500 dark:text-slate-400 dark:hover:text-red-400"
                        >
                          <FiX className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div>
              <Label htmlFor="notes" className="text-sm sm:text-base">
                Session Notes
              </Label>
              <Textarea
                id="notes"
                placeholder="How did the session go? What did you learn? Any key insights..."
                rows={4}
                {...register('notes')}
                className="mt-1"
              />
            </div>

            <div className="flex flex-col sm:flex-row gap-3 pt-4">
              <Button type="submit" className="flex-1 w-full sm:w-auto">
                <FiCheck className="w-4 h-4 mr-2" />
                Log Session
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  reset({
                    date: getTodayDateString(),
                    duration: '',
                    sessionType: 'Rolling',
                    partner: '',
                    notes: '',
                    wins: 0,
                    losses: 0
                  });
                  setTechniques([]);
                }}
                className="w-full sm:w-auto"
              >
                Clear
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default SessionLog;